/**
 * Dashboard page
 * Main overview page with metrics and recent activity
 */

import styles from "./DashboardPage.module.css";
import { Icon } from "@/components/ui/Icon";
import { formatCurrency } from "@/utils";

interface Metric {
  id: string;
  label: string;
  value: string | number;
  icon: string;
  trend?: {
    direction: "up" | "down";
    value: string;
  };
  variant?: "primary" | "warning" | "danger" | "success";
  subtext?: string;
}

interface WorkOrder {
  id: string;
  client: string;
  technician: {
    name: string;
    initials: string;
  };
  status: "pending" | "inProgress" | "completed";
  date: string;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  value: number;
  percentage: number;
}

const metrics: Metric[] = [
  {
    id: "active-services",
    label: "Servicios Activos",
    value: 124,
    icon: "checkCircle",
    trend: { direction: "up", value: "+12%" },
    variant: "primary",
  },
  {
    id: "pending-quotes",
    label: "Presupuestos Pendientes",
    value: 18,
    icon: "document",
    trend: { direction: "down", value: "-5%" },
    variant: "warning",
  },
  {
    id: "stock-alerts",
    label: "Alertas de Stock",
    value: "3 Críticos",
    icon: "alertTriangle",
    variant: "danger",
    subtext: "ADVERTENCIA",
  },
  {
    id: "monthly-revenue",
    label: "Ingresos Mensuales",
    value: 45200,
    icon: "dollarSign",
    trend: { direction: "up", value: "+8%" },
    variant: "success",
  },
];

const workOrders: WorkOrder[] = [
  {
    id: "OT-2824",
    client: "Hotel Meridian",
    technician: { name: "J. Smith", initials: "JS" },
    status: "inProgress",
    date: "24 Ene",
  },
  {
    id: "OT-2823",
    client: "Restaurante Sol",
    technician: { name: "M. García", initials: "MG" },
    status: "completed",
    date: "23 Ene",
  },
  {
    id: "OT-2822",
    client: "Villa Rosa",
    technician: { name: "J. Smith", initials: "JS" },
    status: "pending",
    date: "23 Ene",
  },
  {
    id: "OT-2821",
    client: "Oficina Central",
    technician: { name: "A. Doe", initials: "AD" },
    status: "completed",
    date: "22 Ene",
  },
  {
    id: "OT-2820",
    client: "Almacén Norte",
    technician: { name: "M. García", initials: "MG" },
    status: "inProgress",
    date: "22 Ene",
  },
];

const inventoryItems: InventoryItem[] = [
  {
    id: "1",
    name: "Trampas para Roedores",
    category: "Trampas",
    quantity: 45,
    unit: "unidades",
    value: 2450,
    percentage: 75,
  },
  {
    id: "2",
    name: "Spray Insecticida",
    category: "Insectos",
    quantity: 12,
    unit: "litros",
    value: 890,
    percentage: 30,
  },
  {
    id: "3",
    name: "Cebo Termitas",
    category: "Termitas",
    quantity: 28,
    unit: "kg",
    value: 1680,
    percentage: 60,
  },
  {
    id: "4",
    name: "Kit EPP",
    category: "Seguridad",
    quantity: 8,
    unit: "conjuntos",
    value: 320,
    percentage: 20,
  },
];

const getStatusLabel = (status: WorkOrder["status"]): string => {
  const labels = {
    pending: "Pendiente",
    inProgress: "En Progreso",
    completed: "Completado",
  };
  return labels[status];
};

const getStockLevel = (percentage: number): "low" | "medium" | "high" => {
  if (percentage <= 30) return "low";
  if (percentage <= 60) return "medium";
  return "high";
};

/**
 * Dashboard page component
 */
export function DashboardPage(): React.ReactElement {
  return (
    <div className={styles.dashboard}>
      {/* Metrics */}
      <div className={styles.metrics}>
        {metrics.map((metric) => (
          <div key={metric.id} className={styles.metricCard}>
            <div className={styles.metricHeader}>
              <div
                className={`${styles.metricIcon} ${styles[metric.variant || "primary"]}`}
              >
                <Icon name={metric.icon} size={24} />
              </div>
              {metric.trend && (
                <div
                  className={`${styles.metricTrend} ${styles[metric.trend.direction]}`}
                >
                  {metric.trend.direction === "up" ? "↑" : "↓"}{" "}
                  {metric.trend.value}
                </div>
              )}
            </div>
            <p className={styles.metricLabel}>{metric.label}</p>
            <h3
              className={`${styles.metricValue} ${metric.variant === "danger" ? styles.warning : ""}`}
            >
              {metric.id === "monthly-revenue" &&
              typeof metric.value === "number"
                ? formatCurrency(metric.value)
                : metric.value}
            </h3>
            {metric.subtext && (
              <p className={styles.metricSubtext}>{metric.subtext}</p>
            )}
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className={styles.sections}>
        {/* Recent Work Orders */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>
                Órdenes de Trabajo Recientes
              </h3>
              <p className={styles.sectionSubtitle}>Últimas de tus técnicos</p>
            </div>
            <a href="/workorders" className={styles.viewAllLink}>
              Ver todas →
            </a>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>ID</th>
                  <th>CLIENTE</th>
                  <th>TÉCNICO</th>
                  <th>ESTADO</th>
                  <th>FECHA</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {workOrders.map((order) => (
                  <tr key={order.id}>
                    <td className={styles.workOrderId}>{order.id}</td>
                    <td>{order.client}</td>
                    <td>
                      <div className={styles.technicianBadge}>
                        <div className={styles.technicianAvatar}>
                          {order.technician.initials}
                        </div>
                        {order.technician.name}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[order.status]}`}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td>{order.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Inventory Levels */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Niveles de Inventario</h3>
              <p className={styles.sectionSubtitle}>
                Top 4 productos más utilizados
              </p>
            </div>
          </div>

          <div className={styles.inventoryList}>
            {inventoryItems.map((item) => (
              <div key={item.id} className={styles.inventoryItem}>
                <div className={styles.inventoryInfo}>
                  <h4 className={styles.inventoryName}>{item.name}</h4>
                  <p className={styles.inventoryCategory}>{item.category}</p>
                  <div className={styles.inventoryBar}>
                    <div
                      className={`${styles.inventoryProgress} ${styles[getStockLevel(item.percentage)]}`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
                <div className={styles.inventoryStock}>
                  <p className={styles.inventoryQuantity}>
                    {item.quantity}
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        marginLeft: "4px",
                      }}
                    >
                      {item.unit}
                    </span>
                  </p>
                  <p className={styles.inventoryValue}>
                    {formatCurrency(item.value)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default DashboardPage;
